import type {
    CallExpressionShape,
    Diagnostics,
    Factory,
    ObjectShape,
    Record,
    RecordId,
    Shape,
} from '@servicenow/sdk-build-core'
import { isGUID } from '@servicenow/sdk-build-core'
import { ModuleFunctionShape } from '@servicenow/sdk-build-plugins'
import { OOB_CHANNEL_MAP, SN_AIA_USECASE, SYS_AGENT_ACCESS_ROLE_MAPPING, SYS_USER_ROLE } from './constants'

/**
 * Validates and resolves OOB (Out-of-Box) values against a static map
 * ONLY accepts values that match the static map - shows diagnostic error otherwise
 *
 * @param value - The shape value to resolve
 * @param oobMap - Static map of supported OOB names to sys_ids
 * @param fieldName - Field name for error messages (e.g., 'channel', 'strategy')
 * @param diagnostics - Diagnostics instance for error reporting
 * @returns The resolved sys_id or undefined
 */
export function resolveOOBValue(
    value: Shape | undefined,
    oobMap: { [key: string]: string },
    fieldName: string,
    diagnostics?: Diagnostics
): string | undefined {
    if (!value) {
        return undefined
    }

    // Handle Record references - not supported for OOB fields
    if (value.isRecord()) {
        if (diagnostics) {
            const supportedValues = Object.keys(oobMap)
                .map((k) => `'${k}'`)
                .join(', ')
            diagnostics.error(
                value,
                `${fieldName} field does not support Record references. ` +
                    `Supported OOB values are: ${supportedValues}.`
            )
        }
        return undefined
    }

    // Handle string values
    const rawValue = value.ifString()?.getValue()
    if (!rawValue) {
        return undefined
    }

    // Check if value matches any supported OOB name
    if (oobMap[rawValue]) {
        return oobMap[rawValue]
    }

    // If value looks like a sys_id (32-char hex), pass it through directly
    if (/^[0-9a-f]{32}$/i.test(rawValue)) {
        return rawValue
    }

    // Value not in supported OOB map - show diagnostic error
    if (diagnostics) {
        const supportedValues = Object.keys(oobMap)
            .map((k) => `'${k}'`)
            .join(', ')
        diagnostics.error(
            value,
            `${fieldName} field contains unsupported value '${rawValue}'. ` +
                `Supported OOB values are: ${supportedValues}.`
        )
    }

    return undefined
}

/**
 * Generic helper to resolve String | Record<table> fields
 * Supports three resolution strategies:
 * 1. Record references - extracts sys_id
 * 2. Name mapping - maps human-readable names to sys_ids (optional)
 * 3. Direct sys_id - passes through GUID strings
 * 4. Validation - optionally validates non-GUID strings with diagnostics
 *
 * @param value - The shape value to resolve
 * @param options - Configuration options
 * @returns The resolved sys_id or undefined
 */
export function resolveStringOrRecord(
    value: Shape | undefined,
    options?: {
        /** Optional name-to-sys_id mapping (e.g., OOB_STRATEGY_MAP) */
        nameMap?: { [key: string]: string }
        /** Field name for error messages */
        fieldName?: string
        /** Table name for error messages */
        tableName?: string
        /** Whether to validate that string values are GUIDs */
        requireGuid?: boolean
        /** Diagnostics instance for error reporting */
        diagnostics?: Diagnostics
    }
): string | undefined {
    if (!value) {
        return undefined
    }

    // Handle Record references
    if (value.isRecord()) {
        return value.getId().getValue()
    }

    // Handle string values
    const rawValue = value.ifString()?.getValue()
    if (!rawValue) {
        return undefined
    }

    // Try name mapping first (if provided)
    if (options?.nameMap?.[rawValue]) {
        return options.nameMap[rawValue]
    }

    // If it's a GUID, return as-is
    if (isGUID(rawValue)) {
        return rawValue
    }

    // If GUID is required and value is not a GUID, report error
    if (options?.requireGuid && options.diagnostics) {
        const fieldName = options.fieldName || 'field'
        const tableName = options.tableName || 'table'
        options.diagnostics.error(
            value,
            `${fieldName} field contains non-GUID value '${rawValue}'. ` +
                `If this is a name, please use Record('${tableName}', '${rawValue}') instead, or provide the sys_id directly.`
        )
    }

    // Return the value as-is (might be a sys_id or name depending on context)
    return rawValue
}

/**
 * Resolve a single role value to sys_id
 * IMPORTANT: Only sys_ids (GUIDs) are supported for roleList
 * Role names are NOT supported - must use sys_ids
 * The role_list field is a List type, not a reference field, so role names won't resolve
 */
export function resolveRoleToSysId(roleValue: Shape, diagnostics: Diagnostics): string | undefined {
    return resolveStringOrRecord(roleValue, {
        requireGuid: true,
        fieldName: 'roleList',
        tableName: SYS_USER_ROLE,
        diagnostics,
    })
}

/**
 * Extracts published instructions from versions, validating only one is published
 * @returns Object with publishedInstructions string, error string and errorSource shape if multiple published
 */
export function extractPublishedInstructions(
    aiVersions: Shape | undefined,
    workflowName: string
): { instructions: string; error?: string; errorSource?: Shape } {
    let publishedInstructions = ''
    let publishedCount = 0

    if (aiVersions?.isArray()) {
        const versionElements = aiVersions.getElements()
        for (const versionElement of versionElements) {
            if (versionElement?.isObject()) {
                const state = versionElement.get('state')?.ifString()?.getValue()
                if (state === 'published') {
                    publishedCount++
                    if (publishedCount === 1) {
                        publishedInstructions = versionElement.get('instructions')?.ifString()?.getValue() || ''
                    } else if (publishedCount > 1) {
                        // Point to the second published version's state field as the error source
                        return {
                            instructions: '',
                            error: `AI Agentic Workflow '${workflowName}' has multiple versions with state 'published'. Only one version can be published at a time.`,
                            errorSource: versionElement.get('state') || versionElement,
                        }
                    }
                }
            }
        }
    }

    return { instructions: publishedInstructions }
}

/**
 * Validates trigger configuration based on trigger_flow_definition_type
 * @returns Object with error message and errorSource shape if validation fails, undefined if valid
 */
export function validateTriggerConfig(triggerConfig: ObjectShape): { error: string; errorSource: Shape } | undefined {
    // Validate channel is mandatory
    const channel = triggerConfig.get('channel')
    if (!channel || !channel.ifString()?.getValue()) {
        return { error: `'channel' is mandatory for all triggers`, errorSource: channel || triggerConfig }
    }

    const triggerFlowDefinitionType = triggerConfig.get('triggerFlowDefinitionType')?.ifString()?.getValue()
    const schedule = triggerConfig.get('schedule')?.ifObject()

    if (
        triggerFlowDefinitionType === 'record_create' ||
        triggerFlowDefinitionType === 'record_create_or_update' ||
        triggerFlowDefinitionType === 'record_update'
    ) {
        const triggerCondition = triggerConfig.get('triggerCondition')
        if (!triggerCondition || !triggerCondition.ifString()?.getValue()) {
            return {
                error: `'triggerCondition' is required for trigger type '${triggerFlowDefinitionType}'`,
                errorSource: triggerCondition || triggerConfig,
            }
        }
    } else if (triggerFlowDefinitionType === 'daily') {
        const time = schedule?.get('time')
        if (!time || !time.ifString()?.getValue()) {
            return {
                error: `'schedule.time' is required for trigger type 'daily'`,
                errorSource: time || schedule || triggerConfig,
            }
        }
    } else if (triggerFlowDefinitionType === 'weekly') {
        const time = schedule?.get('time')
        const dayOfWeek = schedule?.get('runDayOfWeek')
        if (!time || !time.ifString()?.getValue()) {
            return {
                error: `'schedule.time' is required for trigger type 'weekly'`,
                errorSource: time || schedule || triggerConfig,
            }
        }
        if (!dayOfWeek || dayOfWeek.getValue() === undefined) {
            return {
                error: `'schedule.runDayOfWeek' is required for trigger type 'weekly'`,
                errorSource: dayOfWeek || schedule || triggerConfig,
            }
        }
    } else if (triggerFlowDefinitionType === 'monthly') {
        const time = schedule?.get('time')
        const day = schedule?.get('runDayOfMonth')
        if (!time || !time.ifString()?.getValue()) {
            return {
                error: `'schedule.time' is required for trigger type 'monthly'`,
                errorSource: time || schedule || triggerConfig,
            }
        }
        if (!day || day.getValue() === undefined) {
            return {
                error: `'schedule.runDayOfMonth' is required for trigger type 'monthly'`,
                errorSource: day || schedule || triggerConfig,
            }
        }
    } else if (triggerFlowDefinitionType === 'email') {
        const targetTable = triggerConfig.get('targetTable')
        if (!targetTable || !targetTable.ifString()?.getValue()) {
            return {
                error: `'targetTable' is required for trigger type 'email'`,
                errorSource: targetTable || triggerConfig,
            }
        }
    }

    // Validate 'starting' field format: YYYY-MM-DD HH:MM:SS
    const starting = schedule?.get('starting')
    if (starting) {
        const startingValue = starting.ifString()?.getValue()
        if (startingValue) {
            const dateTimeRegex = /^\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}$/
            if (!dateTimeRegex.test(startingValue)) {
                return {
                    error: `'schedule.starting' field must be in format YYYY-MM-DD HH:MM:SS (e.g., '2024-01-15 09:00:00'). Got: '${startingValue}'`,
                    errorSource: starting,
                }
            }
        }
    }

    return undefined
}

/**
 * Creates the main workflow record
 */
export async function createWorkflowRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    computedInternalName: string,
    publishedInstructions: string,
    teamRecordId: RecordId | undefined,
    tableName: string,
    aclId?: string
) {
    const arg = callExpression.getArgument(0).asObject()

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        explicitId: arg.get('$id'),
        properties: arg.transform(({ $ }) => ({
            name: $,
            description: $,
            internal_name: $.val(computedInternalName),
            context_processing_script: $.from('contextProcessingScript')
                .map(
                    (v) =>
                        v
                            .if(ModuleFunctionShape)
                            ?.toString(
                                (n) => `${n}({{PARAMS}})`,
                                ['task', 'user_utterance', 'workflow_id', 'context']
                            ) ?? v
                )
                .toCdata()
                .def(''),
            sys_domain: $.from('sysDomain').def('global'),
            strategy: $.val('b264c470ff1022106c36ffffffffff67'),
            execution_mode: $.from('executionMode')
                .map((v) => v || 'copilot')
                .def('copilot'),
            base_plan: $.val(publishedInstructions),
            record_type: $.from('recordType').def('template'),
            team: $.val(teamRecordId),
            active: $.def(true),
            acl: $.val(aclId ?? ''),
        })),
    })
}

/**
 * Creates a team record from nested team object
 */
export async function createTeamRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    teamObj: ObjectShape,
    tableName: string,
    workflowDescription?: Shape
) {
    const teamExplicitId = teamObj.get('$id')

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        explicitId: teamExplicitId,
        properties: teamObj.transform(({ $ }) => ({
            name: $,
            description: $.val(workflowDescription).def(''),
            sys_domain: $.from('sysDomain').def('global'),
            active: $.def(true),
        })),
    })
}

/**
 * Creates team member records from the members array inside team object
 */
export async function createTeamMemberRecords(
    factory: Factory,
    callExpression: CallExpressionShape,
    teamMembers: Shape,
    teamRecordId: RecordId,
    workflowRecordId: RecordId,
    workflowMemoryScope: string,
    workflowDomain: string,
    tableName: string,
    diagnostics: Diagnostics
) {
    const teamMemberRecords: Record[] = []

    if (!teamMembers?.isArray()) {
        return teamMemberRecords
    }

    const teamMemberElements = teamMembers.getElements()

    for (const member of teamMemberElements) {
        // Use resolveStringOrRecord for consistent handling
        const agentSysId = resolveStringOrRecord(member, {
            requireGuid: true,
            fieldName: 'team.members',
            tableName: 'sn_aia_agent',
            diagnostics,
        })

        if (agentSysId) {
            const teamMemberRecord = await factory.createRecord({
                source: callExpression,
                table: tableName,
                properties: {
                    agent: agentSysId,
                    usecase: workflowRecordId,
                    memory_scope: workflowMemoryScope,
                    team: teamRecordId,
                    sys_domain: workflowDomain,
                },
            })
            teamMemberRecords.push(teamMemberRecord)
        }
    }

    return teamMemberRecords
}

/**
 * Creates version records from versions array or single object
 */
export async function createVersionRecords(
    factory: Factory,
    callExpression: CallExpressionShape,
    workflowRecordId: RecordId,
    workflowDomain: Shape,
    aiVersions: Shape | undefined,
    tableName: string
) {
    const versionRecords: Record[] = []

    if (aiVersions?.isArray()) {
        const aiVersionElements = aiVersions.getElements()
        for (const versionElement of aiVersionElements) {
            if (versionElement?.isObject()) {
                const record = await factory.createRecord({
                    source: callExpression,
                    table: tableName,
                    properties: versionElement.transform(({ $ }) => ({
                        target_id: $.val(workflowRecordId),
                        sys_domain: $.val(workflowDomain),
                        version_name: $.from('name'),
                        target_table: $.val(SN_AIA_USECASE),
                        version_number: $.from('number').def(1),
                        instructions: $.from('instructions').def(''),
                        state: $.from('state').def('draft'),
                        condition: $.from('condition').def(''),
                    })),
                })
                versionRecords.push(record)
            }
        }
    }

    return versionRecords
}

/**
 * Creates a trigger configuration record
 */
export async function createTriggerConfigRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    triggerConfig: ObjectShape,
    computedTriggerInternalName: string,
    workflowRecordId: RecordId,
    tableName: string,
    diagnostics: Diagnostics
) {
    const schedule = triggerConfig.get('schedule')?.ifObject()

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: triggerConfig.transform(({ $ }) => ({
            active: $.def(false),
            channel: $.from('channel')
                .map((v) => resolveOOBValue(v, OOB_CHANNEL_MAP, 'channel', diagnostics))
                .def(''),
            condition: $.from('triggerCondition')
                .map((v) => v.if(ModuleFunctionShape)?.toString((n) => `${n}({{PARAMS}})`, ['current']) ?? v)
                .toCdata()
                .def(''),
            description: $.def(''),
            enable_discovery: $.from('enableDiscovery').def(false),
            name: $,
            internal_name: $.val(computedTriggerInternalName),
            notification_script: $.from('notificationScript').def(''),
            objective_template: $.from('objectiveTemplate')
                .map((v) => v.if(ModuleFunctionShape)?.toString((n) => `${n}({{PARAMS}})`, []) ?? v)
                .toCdata()
                .def(''),
            portal: $.from('portal')
                .map((v) =>
                    resolveStringOrRecord(v, {
                        requireGuid: true,
                        fieldName: 'portal',
                        tableName: 'now_assist_deployment_channel',
                        diagnostics,
                    })
                )
                .def(''),
            profile: $.from('profile')
                .map((v) =>
                    resolveStringOrRecord(v, {
                        requireGuid: true,
                        fieldName: 'profile',
                        tableName: 'now_assist_deployment',
                        diagnostics,
                    })
                )
                .def(''),
            run_as: $.from('runAs').def(''),
            run_as_script: $.from('runAsScript')
                .map((v) => v.if(ModuleFunctionShape)?.toString((n) => `${n}({{PARAMS}})`, []) ?? v)
                .toCdata()
                .def(''),
            run_as_user: $.from('runAsUser')
                .map((v) => resolveStringOrRecord(v))
                .def(''),
            business_rule: $.from('businessRule')
                .map((v) => resolveStringOrRecord(v))
                .def(''),
            run_dayofmonth: $.val(schedule?.get('runDayOfMonth')?.getValue()).def(''),
            run_dayofweek: $.val(schedule?.get('runDayOfWeek')?.getValue()).def(''),
            run_period: $.val(schedule?.get('repeatInterval')?.getValue()).def(''),
            run_start: $.val(schedule?.get('starting')?.getValue()).def(''),
            run_time: $.val(schedule?.get('time')?.getValue()).def(''),
            show_notifications: $.from('showNotifications').def(false),
            sys_domain: $.from('domain').def('global'),
            target_table: $.from('targetTable').def(''),
            trigger_flow_definition_type: $.from('triggerFlowDefinitionType').def(''),
            trigger_strategy: $.val(schedule?.get('triggerStrategy')?.getValue()).def(''),
            usecase: $.val(workflowRecordId),
            // usecase_discovery mirrors enable_discovery — both controlled by the single enableDiscovery API field
            usecase_discovery: $.from('enableDiscovery').def(false),
        })),
    })
}

/**
 * Creates a trigger mapping (M2M) record linking workflow to trigger config
 */
export async function createTriggerMappingRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    workflowRecordId: RecordId,
    triggerConfigShape: ObjectShape,
    linkedTriggerConfigId: RecordId,
    tableName: string
) {
    const triggerDomain = triggerConfigShape.get('domain')?.ifString()?.getValue() || 'global'

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: triggerConfigShape.transform(({ $ }) => ({
            active: $.val(false),
            objective_template: $.from('objectiveTemplate')
                .map((v) => v.if(ModuleFunctionShape)?.toString((n) => `${n}({{PARAMS}})`, []) ?? v)
                .toCdata()
                .def(''),
            related_resource_record: $.val(workflowRecordId),
            related_resource_table: $.val(SN_AIA_USECASE),
            sys_domain: $.val(triggerDomain),
            sys_overrides: $.from('sysOverrides')
                .map((v) => resolveStringOrRecord(v))
                .def(''),
            trigger_configuration: $.val(linkedTriggerConfigId),
        })),
    })
}

/**
 * Creates the use case config record
 */
export async function createUseCaseConfigRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    workflowRecordId: RecordId,
    tableName: string
) {
    const arg = callExpression.getArgument(0).asObject()

    return await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: arg.transform(({ $ }) => ({
            active: $.from('active').def(true),
            run_as_user: $.from('runAs').def(''),
            usecase_configuration: $.val(workflowRecordId),
            sys_domain: $.from('sysDomain').def('global'),
        })),
    })
}

/**
 * Creates a data access configuration record and optional M2M role mapping records.
 * Supports `roleList` (sys_ids) and `roleMap` (role names). At least one must be non-empty unless `runAsUser` is set.
 */
export async function createDataAccessConfigRecord(
    factory: Factory,
    callExpression: CallExpressionShape,
    workflowRecordId: RecordId,
    dataAccess: ObjectShape,
    tableName: string,
    diagnostics: Diagnostics,
    hasRunAsUser = false
) {
    const roleListArray = dataAccess.get('roleList')?.ifArray()
    const restrictionGUIDs: string[] = []

    if (roleListArray) {
        const roleElements = roleListArray.getElements()
        for (const roleValue of roleElements) {
            const resolvedSysId = resolveRoleToSysId(roleValue, diagnostics)
            if (resolvedSysId) {
                restrictionGUIDs.push(resolvedSysId)
            }
        }
    }

    const roleListValue = restrictionGUIDs.join(',')

    const roleMapField = dataAccess.get('roleMap')
    const roleMapArray = roleMapField?.ifArray()
    const validatedRoleMapItems: Shape[] = []

    if (roleMapArray) {
        for (const role of roleMapArray.getElements()) {
            if (role.ifString()) {
                const roleValue = role.asString()!.getValue()
                if (isGUID(roleValue)) {
                    diagnostics.error(
                        roleMapField!,
                        `roleMap must contain role names, not sys_ids. ` +
                            `Got: '${roleValue}'. Use the role name (e.g., 'admin'), ` +
                            `or move the sys_id to dataAccess.roleList.`
                    )
                } else if (roleValue.trim() !== '') {
                    validatedRoleMapItems.push(role)
                }
            } else {
                validatedRoleMapItems.push(role)
            }
        }
    }

    if (!hasRunAsUser && restrictionGUIDs.length === 0 && validatedRoleMapItems.length === 0) {
        diagnostics.error(
            dataAccess,
            'dataAccess must have at least one of roleList or roleMap non-empty: ' +
                'provide sys_ids in roleList, role names in roleMap, or both.'
        )
    }

    const roleConfigRecord = await factory.createRecord({
        source: callExpression,
        table: tableName,
        properties: dataAccess.transform(({ $ }) => ({
            agent: $.val(workflowRecordId.getValue()),
            agent_table: $.val(SN_AIA_USECASE),
            action: $.val('limit_to_roles'),
            role_list: $.val(roleListValue),
            description: $.from('description').def(''),
            name: $.val(workflowRecordId.getValue()),
        })),
    })

    const records = [roleConfigRecord]

    for (const role of validatedRoleMapItems) {
        const roleReference = role.ifString()
            ? await factory.createReference({
                  source: role,
                  table: SYS_USER_ROLE,
                  keys: { name: role.asString()!.getValue() },
              })
            : role
        const mappingRecord = await factory.createRecord({
            source: role,
            table: SYS_AGENT_ACCESS_ROLE_MAPPING,
            properties: {
                agent_access_config: roleConfigRecord,
                role: roleReference,
            },
        })
        records.push(mappingRecord)
    }

    return records
}
